import {
    MARK_ALL_NOTIFICATIONS_AS_READ,
    MARK_NOTIFICATION_AS_READ,
    NOTIFICATIONS_QUERY,
    NOTIFICATION_SUBSCRIPTION,
} from "@/graphql/notifications";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { Menu, Transition } from "@headlessui/react";
import {
    BellIcon,
    CheckIcon,
    ClipboardDocumentListIcon,
    EnvelopeIcon,
    MegaphoneIcon,
    ShieldCheckIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { Fragment } from "react";
import { Link } from "react-router-dom";

interface Notification {
    id: string;
    notificationType: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    actionUrl?: string;
    imageUrl?: string;
    sender?: {
        id: string;
        twitchUsername: string;
    };
    isBroadcast: boolean;
    isGroupMessage: boolean;
}

interface NotificationCount {
    total: number;
    unread: number;
}

export default function VillageNotificationBell() {
    const { data, loading, refetch, client } = useQuery<{
        notifications: Notification[];
        notificationCount: NotificationCount;
    }>(NOTIFICATIONS_QUERY, {
        variables: { limit: 10 },
    });

    // Subscribe to new notifications
    useSubscription(NOTIFICATION_SUBSCRIPTION, {
        onData: ({ data: subscriptionData }) => {
            console.debug(
                "[VillageNotificationBell] Subscription data received:",
                subscriptionData,
            );
            if (subscriptionData?.data?.notificationReceived) {
                const newNotification =
                    subscriptionData.data.notificationReceived;
                console.debug(
                    "[VillageNotificationBell] New notification:",
                    newNotification,
                );

                // Add __typename for Apollo cache and ensure all required fields exist
                const notificationWithTypename = {
                    __typename: "NotificationType",
                    imageUrl: null,
                    relatedObjectId: null,
                    relatedObjectType: null,
                    ...newNotification,
                };

                // Directly update the query cache
                try {
                    const existingData = client.cache.readQuery<{
                        notifications: Notification[];
                        notificationCount: NotificationCount;
                    }>({
                        query: NOTIFICATIONS_QUERY,
                        variables: { limit: 10 },
                    });

                    if (existingData) {
                        // Check if notification already exists
                        const exists = existingData.notifications.some(
                            (n) => n.id === newNotification.id,
                        );

                        if (!exists) {
                            client.cache.writeQuery({
                                query: NOTIFICATIONS_QUERY,
                                variables: { limit: 10 },
                                data: {
                                    notifications: [
                                        notificationWithTypename,
                                        ...existingData.notifications,
                                    ].slice(0, 10),
                                    notificationCount: {
                                        __typename: "NotificationCountType",
                                        total:
                                            existingData.notificationCount
                                                .total + 1,
                                        unread:
                                            existingData.notificationCount
                                                .unread + 1,
                                    },
                                },
                            });
                            console.debug(
                                "[VillageNotificationBell] Added notification to cache",
                            );
                        } else {
                            console.debug(
                                "[VillageNotificationBell] Notification already in cache",
                            );
                        }
                    }
                } catch (error) {
                    console.error(
                        "[VillageNotificationBell] Cache update error:",
                        error,
                    );
                    // Fallback to refetch if cache update fails
                    refetch();
                }
            }
        },
        onError: (error) => {
            console.error(
                "[VillageNotificationBell] Subscription error:",
                error,
            );
        },
    });

    const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
        refetchQueries: [
            { query: NOTIFICATIONS_QUERY, variables: { limit: 10 } },
        ],
    });

    const [markAllAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
        refetchQueries: [
            { query: NOTIFICATIONS_QUERY, variables: { limit: 10 } },
        ],
    });

    const notifications = data?.notifications || [];
    const unreadCount = data?.notificationCount?.unread || 0;

    const handleMarkAsRead = async (notificationId: string) => {
        try {
            await markAsRead({ variables: { notificationId } });
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await markAllAsRead();
        } catch (error) {
            console.error("Failed to mark all notifications as read:", error);
        }
    };

    const getNotificationIcon = (type: string) => {
        const iconClass = "h-4 w-4";
        switch (type) {
            case "stream_team_invitation":
                return (
                    <EnvelopeIcon className={`${iconClass} text-purple-400`} />
                );
            case "stream_team_acceptance":
                return <CheckIcon className={`${iconClass} text-green-400`} />;
            case "stream_team_request":
                return (
                    <UserGroupIcon className={`${iconClass} text-blue-400`} />
                );
            case "stream_team_application_status":
                return (
                    <ClipboardDocumentListIcon
                        className={`${iconClass} text-yellow-400`}
                    />
                );
            case "admin_message":
                return (
                    <MegaphoneIcon className={`${iconClass} text-orange-400`} />
                );
            case "shield_tag_earned":
                return (
                    <ShieldCheckIcon
                        className={`${iconClass} text-amber-400`}
                    />
                );
            case "system_announcement":
                return (
                    <MegaphoneIcon className={`${iconClass} text-red-400`} />
                );
            default:
                return <BellIcon className={`${iconClass} text-slate-400`} />;
        }
    };

    return (
        <Menu as="div" className="relative">
            <Menu.Button className="text-brand hover:text-brand relative p-2">
                <span className="sr-only">View notifications</span>
                <BellIcon className="h-6 w-6" aria-hidden="true" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="ring-brand bg-brand_charcoal absolute -right-3.5 z-30 mt-2.5 max-h-[500px] w-96 origin-top-right overflow-y-auto rounded-md py-2 shadow-lg ring-1 focus:outline-hidden">
                    <div className="border-brand border-b px-4 py-2">
                        <div className="flex items-center justify-between">
                            <h3 className="text-brand text-sm font-semibold">
                                Notifications
                            </h3>
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllAsRead}
                                    className="text-brand text-xs hover:underline"
                                >
                                    Mark all as read
                                </button>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="text-brand px-4 py-8 text-center text-sm">
                            Loading notifications...
                        </div>
                    )}

                    {!loading && notifications.length === 0 && (
                        <div className="text-brand px-4 py-8 text-center text-sm">
                            No notifications yet
                        </div>
                    )}

                    {!loading &&
                        notifications.map((notification) => (
                            <Menu.Item key={notification.id}>
                                {({ active }) => {
                                    const content = (
                                        <div
                                            className={`${
                                                active ? "bg-brand/10" : ""
                                            } ${
                                                !notification.isRead
                                                    ? "bg-brand/5"
                                                    : ""
                                            } border-brand/20 cursor-pointer border-b px-4 py-3`}
                                        >
                                            <NotificationContent
                                                notification={notification}
                                                getNotificationIcon={
                                                    getNotificationIcon
                                                }
                                            />
                                        </div>
                                    );

                                    return (
                                        <Link
                                            to={`/dashboard/inbox?notification=${notification.id}`}
                                            className="block"
                                        >
                                            {content}
                                        </Link>
                                    );
                                }}
                            </Menu.Item>
                        ))}

                    {!loading && notifications.length > 0 && (
                        <div className="border-brand border-t px-4 py-2">
                            <Link
                                to="/dashboard/inbox"
                                className="text-brand block text-center text-sm hover:underline"
                            >
                                View All Messages
                            </Link>
                        </div>
                    )}
                </Menu.Items>
            </Transition>
        </Menu>
    );
}

function NotificationContent({
    notification,
    getNotificationIcon,
}: {
    notification: Notification;
    getNotificationIcon: (type: string) => React.JSX.Element;
}) {
    return (
        <div className="flex gap-3">
            <div className="mt-1 shrink-0">
                {getNotificationIcon(notification.notificationType)}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <p
                        className={`text-sm ${
                            notification.isRead
                                ? "text-brand/70"
                                : "text-brand font-semibold"
                        }`}
                    >
                        {notification.title}
                    </p>
                    {!notification.isRead && (
                        <span className="bg-brand h-2 w-2 shrink-0 rounded-full"></span>
                    )}
                </div>
                <p className="text-brand/60 mt-1 line-clamp-2 text-xs">
                    {notification.message}
                </p>
                <p className="text-brand/50 mt-1 text-xs">
                    {formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                    })}
                </p>
            </div>
        </div>
    );
}
